import { getMs } from '@/helpers/time-utils'
import type { T_AuthSecurity } from './steps/authorize-ticket'


const PAID_CALL_SESSION_KEY = 'ERA_PAID_CALL_SESSION'
export const PAID_CALL_SESSION_TTL_MS = getMs('5m')

export type T_PaidCallStep = 'outgoingCall' | 'incomingCallOrSms'

export type T_PaidCallSession = {
  issuedAt: number
  expiresAt: number
  step: T_PaidCallStep
  userPhone: string
  outgoingPhone: string
  outgoingMessageNumber: string | null
  ticketId: string | null
  authSecurity: T_AuthSecurity | null
}

const isPaidCallStep = (step: unknown): step is T_PaidCallStep => (
  step === 'outgoingCall' || step === 'incomingCallOrSms'
)

export const readPaidCallSession = (): T_PaidCallSession | null => {
  try {
    const raw = sessionStorage.getItem(PAID_CALL_SESSION_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<T_PaidCallSession>
    const isFresh = typeof parsed.expiresAt === 'number' && Date.now() < parsed.expiresAt

    if (!isFresh || !parsed.outgoingPhone || !isPaidCallStep(parsed.step) || typeof parsed.issuedAt !== 'number') {
      sessionStorage.removeItem(PAID_CALL_SESSION_KEY)
      return null
    }

    return parsed as T_PaidCallSession
  } catch {
    return null
  }
}

export const savePaidCallSession = (
  session: Omit<T_PaidCallSession, 'issuedAt' | 'expiresAt'> & { issuedAt?: number },
) => {
  const issuedAt = session.issuedAt ?? Date.now()
  const payload: T_PaidCallSession = {
    issuedAt,
    expiresAt: issuedAt + PAID_CALL_SESSION_TTL_MS,
    step: session.step,
    userPhone: session.userPhone,
    outgoingPhone: session.outgoingPhone,
    outgoingMessageNumber: session.outgoingMessageNumber,
    ticketId: session.ticketId,
    authSecurity: session.authSecurity,
  }

  try {
    sessionStorage.setItem(PAID_CALL_SESSION_KEY, JSON.stringify(payload))
  } catch {
    // sessionStorage may be unavailable
  }
}

export const clearPaidCallSession = () => {
  try {
    sessionStorage.removeItem(PAID_CALL_SESSION_KEY)
  } catch {
    // sessionStorage may be unavailable
  }
}

export const touchPaidCallStep = (step: T_PaidCallStep) => {
  const current = readPaidCallSession()
  if (!current) return

  savePaidCallSession({
    ...current,
    step,
  })
}
